import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DxTextBoxModule } from 'devextreme-angular';
import { AppGlobals } from 'src/app/app.globals';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { ValidationToastComponent } from 'src/app/components/validation-toast/validation-toast.component';
import { GenericPopupComponent, PopupButton } from 'src/app/components/generic-popup/generic-popup.component';
import { SandboxService } from 'src/app/services/sandbox.service';

type CardState = 'gated' | 'empty' | 'linked';

@Component({
    selector: 'app-sharepoint-config',
    imports: [
        CommonModule,
        FormsModule,
        GpdButtonComponent,
        SvgIconComponent,
        ValidationToastComponent,
        GenericPopupComponent,
        DxTextBoxModule,
    ],
    templateUrl: './sharepoint-config.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./sharepoint-config.component.css']
})
export class SharepointConfigComponent implements OnInit {
  @ViewChild('successToast') successToast!: ValidationToastComponent;
  @ViewChild('errorToast') errorToast!: ValidationToastComponent;
  @ViewChild('infoToast') infoToast!: ValidationToastComponent;
  @ViewChild('copyToast') copyToast!: ValidationToastComponent;

  mainConnectionHealthy = false;
  currentUrl: string | null = null;
  validatedAt: string | null = null;
  urlInput = '';

  errorDetails = '';
  errorDetailsPopupVisible = false;
  removeConfirmVisible = false;
  removeLinkTitle = $localize`:@@removeSandboxLinkTitle:Remove sandbox folder link`;

  gateTooltip = $localize`:@@sandboxLinkGateTooltip:A sandbox folder link can only be added if a folder link is already present.`;
  helpTooltip = $localize`:@@sandboxLinkHelpTooltip:Project documents that are currently being worked on and are not yet available in their final version can be stored in a separate SharePoint folder called "Sandbox". You can configure the structure of the Sandbox folder in the "Folders" area.`;

  readonly errorActionText = $localize`:@@errorDetails:Error details`;
  readonly hintCopyLink = $localize`:@@copyLink:Copy link`;

  private readonly msgSuccessTitle = $localize`:@@sandboxValidationSuccessTitle:Validation successful`;
  private readonly msgSuccessBody = $localize`:@@sandboxValidationSuccessText:SharePoint connection successfully established.`;
  private readonly msgErrorTitle = $localize`:@@sandboxValidationFailedTitle:Validation failed`;
  private readonly msgErrorBody = $localize`:@@sandboxValidationFailedText:An error has occurred. Please contact your SharePoint administrator and provide the error details.`;
  private readonly msgCopied = $localize`:@@copiedToClipboard:Copied to clipboard`;
  readonly msgRemoveWarning = $localize`:@@removeSandboxLinkConfirmHint:This will remove all existing project folder connections. You will need to re-link them manually for each project.`;
  private readonly msgReplaceInfoTitle = $localize`:@@sandboxLinkReplaceInfoTitle:Connections will be lost`;
  private readonly msgReplaceInfo = $localize`:@@sandboxLinkReplaceInfo:Replacing the link will remove all existing project folder connections. You will need to re-link them manually for each project.`;

  private replaceInfoShown = false;

  private saving = false;

  constructor(private readonly service: SandboxService) {}

  get isSaving(): boolean {
    return this.saving;
  }

  get cardState(): CardState {
    if (!this.mainConnectionHealthy) return 'gated';
    return this.currentUrl ? 'linked' : 'empty';
  }

  get validateDisabled(): boolean {
    return this.urlInput.trim().length === 0 || this.saving;
  }

  ngOnInit(): void {
    this.reload();
  }

  async reload(): Promise<void> {
    try {
      const result = await this.service.getSandboxSettings({});
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      this.mainConnectionHealthy = result.mainConnectionHealthy === true;
      this.currentUrl = result.url ?? null;
      this.validatedAt = result.validatedAt ?? null;
      this.urlInput = '';
      this.replaceInfoShown = false;
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  onUrlInputChanged(): void {
    if (this.cardState === 'linked' && this.urlInput.trim().length > 0 && !this.replaceInfoShown) {
      this.replaceInfoShown = true;
      this.infoToast.showToast(this.msgReplaceInfo, 'Warning', this.msgReplaceInfoTitle);
    }
  }

  async validateLink(): Promise<void> {
    if (this.validateDisabled) return;
    this.errorToast?.closeToast();
    this.errorDetails = '';
    this.saving = true;
    try {
      const result = await this.service.saveSandboxLink({ Url: this.urlInput.trim() });
      if (result.hasError === true) {
        this.errorDetails = result.errorDetails ?? result.feedback ?? '';
        this.errorToast.showToast(this.msgErrorBody, 'Error', this.msgErrorTitle);
        return;
      }
      this.successToast.showToast(this.msgSuccessBody, 'Success', this.msgSuccessTitle);
      await this.reload();
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    } finally {
      this.saving = false;
    }
  }

  showErrorDetails(): void {
    this.errorDetailsPopupVisible = true;
  }

  get removePopupButtons(): PopupButton[] {
    return [
      {
        text: $localize`:@@cancel:Cancel`,
        type: 'tertiary-text',
        onClick: () => { this.removeConfirmVisible = false; },
      },
      {
        text: $localize`:@@remove:Remove`,
        type: 'danger',
        onClick: () => this.confirmRemoveLink(),
      },
    ];
  }

  get errorPopupButtons(): PopupButton[] {
    return [
      {
        text: $localize`:@@copy:Copy`,
        type: 'primary',
        onClick: () => this.copyErrorDetails(),
      },
    ];
  }

  async copyCurrentUrl(): Promise<void> {
    if (!this.currentUrl) return;
    try {
      await navigator.clipboard.writeText(this.currentUrl);
      this.copyToast.showToast(this.msgCopied, 'Success');
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  async copyErrorDetails(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.errorDetails);
      this.copyToast.showToast(this.msgCopied, 'Success');
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  initRemoveLink(): void {
    this.removeConfirmVisible = true;
  }

  async confirmRemoveLink(): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    try {
      const result = await this.service.removeSandboxLink({});
      if (result.hasError === true) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      await this.reload();
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error);
    } finally {
      this.removeConfirmVisible = false;
      this.saving = false;
    }
  }

}
